import { cloneElement, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { createPortal } from "react-dom";

import { applyPlacement, placeFloating } from "./Popover";
import styles from "./Tooltip.module.css";

export interface TooltipProps {
  /** The hint (from `t()`), e.g. the mockup's "Recargar" or "Modo enfoque  Mayús+\". */
  readonly content: string;
  /** The control it describes; it receives `aria-describedby` while the hint shows. */
  readonly children: ReactElement<{ "aria-describedby"?: string | undefined }>;
  /** Hover delay; the keyboard focus shows the hint at once. Injectable for tests. */
  readonly delayMs?: number | undefined;
}

/** The default hover delay, long enough not to flash while the pointer crosses a toolbar. */
export const TOOLTIP_DELAY_MS = 400;

/**
 * How long the hint survives the pointer leaving its control: long enough to move onto the hint
 * itself, which then keeps it open (WCAG 1.4.13 "hoverable", verifier m5).
 */
export const TOOLTIP_HIDE_GRACE_MS = 150;

/**
 * A short description of a control, shown on keyboard focus at once and on hover after a delay (the
 * mockup's `title` hints and `.tip`).
 *
 * `role="tooltip"` and `aria-describedby` on the control, only while it shows, so a screen reader
 * hears the hint once and no id dangles. It never carries the only name of a control: an icon-only
 * button still needs its own `aria-label`. Escape hides it without moving the focus. The hint is
 * portalled and placed above the control, flipping below when there is no room.
 */
export function Tooltip({ content, children, delayMs = TOOLTIP_DELAY_MS }: TooltipProps): ReactElement {
  const [visible, setVisible] = useState(false);
  const id = useId();
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cancel = (): void => {
    clearTimeout(timer.current);
    timer.current = undefined;
  };
  const hide = (): void => {
    cancel();
    setVisible(false);
  };
  const hideLater = (): void => {
    cancel();
    timer.current = setTimeout(() => {
      setVisible(false);
    }, TOOLTIP_HIDE_GRACE_MS);
  };
  // The control's own description stays; the hint's id is added next to it while it shows (m3).
  const own = children.props["aria-describedby"];
  const describedBy = visible ? [own, id].filter((part) => part !== undefined && part !== "").join(" ") : own;

  useEffect(() => cancel, []);

  useEffect(() => {
    if (!visible) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setVisible(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [visible]);

  useLayoutEffect(() => {
    const tip = tipRef.current;
    const wrapper = wrapperRef.current;
    if (!visible || tip === null || wrapper === null) return;
    applyPlacement(
      tip,
      placeFloating(
        wrapper.getBoundingClientRect(),
        { width: tip.offsetWidth, height: tip.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
        "center",
        "above",
      ),
    );
  }, [visible]);

  return (
    <span
      ref={wrapperRef}
      className={styles.anchor}
      onMouseEnter={() => {
        cancel();
        timer.current = setTimeout(() => {
          setVisible(true);
        }, delayMs);
      }}
      onMouseLeave={hideLater}
      // The hint is a React child of this span, portal or not, so entering it counts as staying
      // here: React synthesises enter and leave along the component tree (WCAG 1.4.13 hoverable).
      onFocus={() => {
        cancel();
        setVisible(true);
      }}
      onBlur={hide}
    >
      {cloneElement(children, { "aria-describedby": describedBy })}
      {visible
        ? createPortal(
            <div ref={tipRef} id={id} role="tooltip" className={styles.tooltip}>
              {content}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
